import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Json } from '@/integrations/supabase/types';
import { layoutSaveRefusalMessage } from '@/lib/facility/layoutSaveRefusal';

export interface ParkingLotLayout {
  id: string;
  shop_id: string;
  name: string;
  center_lng: number | null;
  center_lat: number | null;
  zoom: number | null;
  bearing: number | null;
  pitch: number | null;
}

export interface ParkingLotSpot {
  id: string;
  shop_id: string;
  layout_id: string;
  label: string;
  spot_type: string;
  geom: any; // GeoJSON polygon (legacy)
  geom_px: number[][][] | null; // Pixel coordinates polygon
  order_index: number;
  is_active: boolean;
  ticketId?: string | null;
}

export interface ParkingLotTicket {
  id: string;
  vehicleInfo: string;
  customer_name: string;
  status: string;
  parking_spot_id: string | null;
  parking_spot?: { id: string; label: string } | null;
}

export interface ParkingLotImage {
  id: string;
  shop_id: string;
  image_path: string;
  image_width: number;
  image_height: number;
  reference_points: any[];
  rotation_deg: number;
  scale_ft_per_pixel: number | null;
  status: 'PENDING' | 'CALIBRATED' | 'ACTIVE';
}

export function useParkingLotMap(shopId: string | undefined) {
  const [layout, setLayout] = useState<ParkingLotLayout | null>(null);
  const [spots, setSpots] = useState<ParkingLotSpot[]>([]);
  const [tickets, setTickets] = useState<ParkingLotTicket[]>([]);
  const [parkingLotImage, setParkingLotImage] = useState<ParkingLotImage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Fetch layout, spots, tickets, and image
  const fetchData = useCallback(async () => {
    if (!shopId) return;
    
    setIsLoading(true);
    try {
      // Fetch parking lot image (get the ACTIVE one, or most recent)
      const { data: imageData, error: imageError } = await supabase
        .from('parking_lot_images')
        .select('*')
        .eq('shop_id', shopId)
        .order('status', { ascending: false }) // ACTIVE first, then CALIBRATED, then PENDING
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (imageError) throw imageError;

      if (imageData) {
        setParkingLotImage({
          id: imageData.id,
          shop_id: imageData.shop_id,
          image_path: imageData.image_path,
          image_width: imageData.image_width,
          image_height: imageData.image_height,
          reference_points: (imageData.reference_points as any[]) || [],
          rotation_deg: imageData.rotation_deg || 0,
          scale_ft_per_pixel: imageData.scale_ft_per_pixel,
          status: imageData.status as 'PENDING' | 'CALIBRATED' | 'ACTIVE',
        });
      } else {
        setParkingLotImage(null);
      }

      // Fetch layout
      const { data: layoutDataRaw, error: layoutError } = await supabase
        .from('parking_lot_layouts')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();

      if (layoutError) throw layoutError;

      const layoutData: ParkingLotLayout | null = Array.isArray(layoutDataRaw)
        ? ((layoutDataRaw[0] as ParkingLotLayout) ?? null)
        : ((layoutDataRaw as ParkingLotLayout | null) ?? null);

      setLayout(layoutData);

      // Fetch spots if layout exists
      if (layoutData?.id) {
        const { data: spotsData, error: spotsError } = await supabase
          .from('parking_lot_spots')
          .select('*')
          .eq('layout_id', layoutData.id)
          .order('order_index');

        if (spotsError) throw spotsError;

        const mappedSpots: ParkingLotSpot[] = (spotsData || []).map((s: any) => ({
          ...s,
          geom: s.geom,
          geom_px: s.geom_px as number[][][] | null,
        }));
        setSpots(mappedSpots);
      } else {
        setSpots([]);
      }

      // Fetch parking lot tickets.
      // Chronomancer R-LOC-2 + R-LOC-8: filter must mirror Birdseye/derived-
      // presence semantics AND the canonical "chain root claims" rule.
      //   - picked_up_at IS NULL: a freshly-departed vehicle briefly carrying
      //     stale parking_lot=true would render as occupying the lot.
      //   - status NOT IN (Cancelled, Deleted): defence-in-depth — terminal
      //     tickets that escaped location-clear should not appear.
      //   - rollover_of_ticket_id IS NULL: future-day Part B tickets inherit
      //     their root's parking_spot_id, but the root is the physical
      //     vehicle today; rendering Part B as a separate parked vehicle
      //     causes the same spot to count twice (or hide the actual
      //     occupant if the spot card uses .find).
      const ticketsResult = await supabase
        .from('tickets')
        .select('id, status, parking_spot_id, vehicle_id, customer_name')
        .eq('shop_id', shopId)
        .eq('parking_lot', true)
        .is('picked_up_at', null)
        .is('rollover_of_ticket_id', null)
        // DMC-D3b: canonical active-ticket filter — both reps excluded.
        // Status IN excludes Cancelled/Deleted; deleted_at null is the
        // defense-in-depth ghost-row guard so a stale parking-lot
        // assignment cannot keep a vehicle visually occupying a spot.
        .not('status', 'in', '("Cancelled","Deleted")')
        .is('deleted_at', null);
      
      const ticketsData = ticketsResult.data as Array<{
        id: string;
        status: string;
        parking_spot_id: string | null;
        vehicle_id: string | null;
        customer_name: string | null;
      }> | null;
      const ticketsError = ticketsResult.error;

      if (ticketsError) throw ticketsError;
      
      // Fetch vehicle info for tickets
      const vehicleIds = (ticketsData || []).map(t => t.vehicle_id).filter(Boolean);
      let vehicleMap: Record<string, { year: number | null; make: string | null; model: string | null }> = {};
      
      if (vehicleIds.length > 0) {
        const { data: vehiclesData } = await supabase
          .from('vehicles')
          .select('id, year, make, model')
          .in('id', vehicleIds);
        
        vehicleMap = (vehiclesData || []).reduce((acc, v) => {
          acc[v.id] = { year: v.year, make: v.make, model: v.model };
          return acc;
        }, {} as Record<string, { year: number | null; make: string | null; model: string | null }>);
      }

      // Map to our interface
      const mappedTickets: ParkingLotTicket[] = (ticketsData || []).map((t) => {
        const vehicle = t.vehicle_id ? vehicleMap[t.vehicle_id] : null;
        return {
          id: t.id,
          vehicleInfo: vehicle ? [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') : 'Unknown Vehicle',
          customer_name: t.customer_name || 'Unknown Customer',
          status: t.status,
          parking_spot_id: t.parking_spot_id,
          parking_spot: null,
        };
      });
      setTickets(mappedTickets);

    } catch (error) {
      console.error('Error fetching parking lot data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load parking lot data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [shopId, toast]);

  // Save layout position
  const saveLayoutPosition = useCallback(async (position: {
    center_lng: number;
    center_lat: number;
    zoom: number;
    bearing: number;
    pitch: number;
  }) => {
    if (!shopId) return;

    try {
      const layoutId = await supabase.rpc('upsert_parking_lot_layout', {
        p_shop_id: shopId,
        p_layout_id: layout?.id ?? null,
        p_center_lng: position.center_lng,
        p_center_lat: position.center_lat,
        p_zoom: position.zoom,
        p_bearing: position.bearing,
        p_pitch: position.pitch,
      });

      if (layoutId) {
        setLayout(prev => prev ? { ...prev, ...position } : null);
      }
    } catch (error) {
      console.error('Error saving layout position:', error);
    }
  }, [shopId, layout?.id]);

  // Save spots (supports both geom and geom_px)
  const saveSpots = useCallback(async (newSpots: Array<{
    id?: string;
    label: string;
    spot_type?: string;
    geom?: any;
    geom_px?: number[][][];
    is_active?: boolean;
  }>) => {
    if (!shopId) {
      toast({
        title: 'Error',
        description: 'No shop selected',
        variant: 'destructive',
      });
      return false;
    }

    // Ensure we have a layout
    let currentLayoutId = layout?.id;
    if (!currentLayoutId) {
      // Create a layout first
      try {
        const { data: newLayoutId } = await supabase.rpc('upsert_parking_lot_layout', {
          p_shop_id: shopId,
          p_layout_id: null,
          p_center_lng: 0,
          p_center_lat: 0,
          p_zoom: 18,
          p_bearing: 0,
          p_pitch: 0,
        });
        currentLayoutId = newLayoutId;
      } catch (err) {
        console.error('Failed to create layout:', err);
        toast({
          title: 'Error',
          description: 'Failed to create parking lot layout',
          variant: 'destructive',
        });
        return false;
      }
    }

    try {
      // Cast spots to Json for RPC call
      const spotsJson = newSpots.map((s, index) => ({
        // Stage 0D — an existing spot is edited in place (same id, same
        // parked vehicle); an unsaved spot's temp id is not sent.
        ...(s.id && !s.id.startsWith('temp-') ? { id: s.id } : {}),
        label: s.label,
        spot_type: s.spot_type || 'STANDARD',
        geom: s.geom || s.geom_px || null, // Use geom_px as geom if no geom provided
        geom_px: s.geom_px || null,
        is_active: s.is_active ?? true,
        order_index: index,
      }));

      // Stage 0D — the RPC's refusal used to be ignored (supabase-js returns
      // it, it does not throw), so a refused save still said "saved".
      const { error: saveError } = await supabase.rpc('replace_parking_lot_spots', {
        p_shop_id: shopId,
        p_layout_id: currentLayoutId,
        p_spots: spotsJson as unknown as Json,
      });
      if (saveError) throw new Error(layoutSaveRefusalMessage(saveError.message));

      await fetchData();
      toast({
        title: 'Success',
        description: 'Parking spots saved',
      });
      return true;
    } catch (error: any) {
      console.error('Error saving spots:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save parking spots',
        variant: 'destructive',
      });
      return false;
    }
  }, [shopId, layout?.id, fetchData, toast]);

  // Assign ticket to spot using canonical move_vehicle_location RPC
  const assignTicketToSpot = useCallback(async (ticketId: string, spotId: string | null) => {
    if (!shopId) return false;

    try {
      const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
        p_ticket_id: ticketId,
        p_target_parking_spot_id: spotId,
      });

      if (error) throw error;

      const result = rawData as unknown as { success: boolean; reason?: string };
      if (result && !result.success) {
        toast({
          title: 'Cannot assign spot',
          description: result.reason || 'Spot unavailable',
          variant: 'destructive',
        });
        return false;
      }

      await fetchData();
      return true;
    } catch (error: any) {
      console.error('Error assigning ticket to spot:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to assign ticket',
        variant: 'destructive',
      });
      return false;
    }
  }, [shopId, fetchData, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    layout,
    spots,
    tickets,
    parkingLotImage,
    isLoading,
    fetchData,
    saveLayoutPosition,
    saveSpots,
    assignTicketToSpot,
  };
}
